// Persistent left navigation for the catalog_manager console (migration
// 025's third staff role). Rendered ONLY inside AppShell when the live
// session's role is 'catalog_manager' (AppShell.jsx decides this, mirroring
// how it already gates AdminNav for 'admin' and SalesNav for 'sales') — an
// admin or sales session never mounts this component, matching those two
// components' own header comments on the reverse case. Every route it
// links to is still independently guarded server-side and, in the SPA, by
// each route's own RequireRole — this component's presence is a
// navigation/UX choice, not a security boundary (admin/CLAUDE.md).
//
// Mirrors AdminNav.jsx's structure/styling (not SalesNav.jsx) — this role's
// screens are catalog-shaped, closer to admin's visual language, per this
// task's own brief. Four items only: Catalog, Categories, Cost management
// (the three existing admin console screens this role is now dual-role
// reachable on, see routes/adminCatalogRoutes.js's own comment), and
// Incoming requests (this role's own new, narrow, read-only request read —
// see routes/catalogManagerRequestRoutes.js). Deliberately NOT linking
// approval queue, all-proformas, users, customers, bank-accounts, settings,
// or quotations — this role has no access to any of those, server-side or
// otherwise.
//
// '/incoming-requests' (not '/catalog-manager/requests' or anything
// starting with '/admin'/'/catalog'/'/auth'/'/sales'/'/guest'/'/verify') —
// same class of dev-proxy/Nginx path-prefix collision AdminNav.jsx's own
// header comment describes, now also true of '/catalog-manager' itself
// (see admin/vite.config.js's own comment on that prefix, added for this
// role's GET /catalog-manager/requests[/:id]). A client route literally
// under that prefix would hit the same "raw backend JSON instead of the
// SPA" bug AdminNav's comment describes for '/admin/requests'.

import { NavLink } from 'react-router-dom';
import { Package, FolderTree, Coins, Inbox } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext.jsx';
import styles from './CatalogManagerNav.module.css';

// Catalog/Categories/Cost management reuse the SAME i18n ids AdminNav.jsx
// already uses for these three screens — their copy ("Catalog",
// "Categories & attributes", "Cost management") is generic, not phrased as
// admin-specific, and the screens themselves are the exact same page
// components (CatalogPage/CategoriesPage/CostManagementPage) reached via the
// exact same routes. Only "Incoming requests" is new.
const ITEMS = [
  { to: '/products', labelId: 'nav.admin.catalog', Icon: Package, end: true },
  { to: '/categories', labelId: 'nav.admin.categories', Icon: FolderTree },
  { to: '/cost-management', labelId: 'nav.admin.costManagement', Icon: Coins },
  { to: '/incoming-requests', labelId: 'nav.catalogManager.incomingRequests', Icon: Inbox },
];

export default function CatalogManagerNav() {
  const { t } = useLanguage();

  return (
    <nav className={styles.nav} aria-label={t('nav.admin.catalog')}>
      <ul className={styles.list}>
        {ITEMS.map(({ to, labelId, Icon, end }) => (
          <li key={to}>
            <NavLink
              to={to}
              end={end}
              className={({ isActive }) => `${styles.link} ${isActive ? styles.linkActive : ''}`}
            >
              <Icon size={18} aria-hidden="true" />
              {t(labelId)}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
